import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

function MeddpiccGauge({ label, score, analysis, action }) {
  const color = score >= 7 ? 'bg-green-500' : score >= 4 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-white text-xs font-semibold">{label}</span>
        <span className={`${color} text-white text-xs px-2 py-0.5 rounded-full font-bold`}>{score}/10</span>
      </div>
      <div className="w-full bg-slate-700 rounded-full h-1.5 mb-2">
        <div className={`${color} h-1.5 rounded-full`} style={{ width: `${score * 10}%` }} />
      </div>
      <p className="text-slate-400 text-[11px] leading-relaxed">{analysis}</p>
      {action && <p className="text-cyan-400 text-[11px] mt-1 font-medium">{action}</p>}
    </div>
  );
}

export default function DealStrategistPage() {
  const [analyses, setAnalyses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [leads, setLeads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ lead_id: '', quote_id: '' });

  const fetchAll = useCallback(async () => {
    const [aRes, lRes, qRes] = await Promise.all([
      fetch(`${API}/api/deal-strategy`, { credentials: 'include' }),
      fetch(`${API}/api/leads`, { credentials: 'include' }),
      fetch(`${API}/api/quotes`, { credentials: 'include' })
    ]);
    if (aRes.ok) setAnalyses(await aRes.json());
    if (lRes.ok) { const d = await lRes.json(); setLeads(Array.isArray(d) ? d : d.leads || []); }
    if (qRes.ok) { const d = await qRes.json(); setQuotes(Array.isArray(d) ? d : d.quotes || []); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 5000); };

  const handleAnalyze = async () => {
    if (!form.lead_id && !form.quote_id) { showMsg('Seleccione un lead o cotizacion'); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/deal-strategy/analyze`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        const data = await res.json();
        setSelected(data);
        showMsg('Analisis MEDDPICC completado');
        fetchAll();
      } else { showMsg('Error al analizar'); }
    } catch (e) { showMsg('Error de conexion'); }
    setGenerating(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar?')) return;
    await fetch(`${API}/api/deal-strategy/${id}`, { method: 'DELETE', credentials: 'include' });
    if (selected?.id === id) setSelected(null);
    fetchAll();
  };

  const a = selected?.analysis || {};
  const m = a.meddpicc_score || {};
  const probColors = { Alto: 'text-green-400 bg-green-900/30', Medio: 'text-amber-400 bg-amber-900/30', Bajo: 'text-red-400 bg-red-900/30' };

  return (
    <div data-testid="deal-strategist-page" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Deal Strategist IA</h1>
          <p className="text-slate-400 text-sm">Calificacion MEDDPICC automatica con probabilidad de cierre y plan de accion</p>
        </div>
      </div>
      {msg && <div className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded text-sm">{msg}</div>}

      {/* Generate Form */}
      <Card className="bg-slate-900/60 border-slate-700">
        <CardContent className="pt-5">
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-slate-400 text-xs block mb-1">Lead</label>
              <select data-testid="deal-lead-select" className="w-full bg-slate-800 text-white border border-slate-600 rounded px-3 py-2 text-sm"
                value={form.lead_id} onChange={e => setForm(f => ({ ...f, lead_id: e.target.value }))}>
                <option value="">-- Seleccionar Lead --</option>
                {leads.map(l => <option key={l.id} value={l.id}>{l.name} - {l.service_interest || l.source} ({l.pipeline_step || l.status})</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-slate-400 text-xs block mb-1">Cotizacion (opcional)</label>
              <select className="w-full bg-slate-800 text-white border border-slate-600 rounded px-3 py-2 text-sm"
                value={form.quote_id} onChange={e => setForm(f => ({ ...f, quote_id: e.target.value }))}>
                <option value="">-- Sin cotizacion --</option>
                {quotes.map(q => <option key={q.id} value={q.id}>#{q.quote_number} - {q.client_name} (${q.total?.toLocaleString()})</option>)}
              </select>
            </div>
            <Button data-testid="analyze-deal-btn" onClick={handleAnalyze} disabled={generating} className="bg-violet-600 hover:bg-violet-700 text-white px-6">
              {generating ? 'Analizando...' : 'Analizar MEDDPICC'}
            </Button>
          </div>
          {generating && (
            <div className="mt-3 flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-violet-300 text-sm">Analizando oportunidad con GPT-4o...</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Analysis List */}
        <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
          <p className="text-slate-400 text-xs font-semibold uppercase">Analisis Realizados ({analyses.length})</p>
          {analyses.map(an => (
            <div key={an.id} onClick={() => setSelected(an)}
              className={`bg-slate-800/60 border rounded-lg p-3 cursor-pointer hover:bg-slate-700/60 ${selected?.id === an.id ? 'border-violet-500' : 'border-slate-700'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white text-sm font-medium">{an.lead_name || 'Deal'}</p>
                  <p className="text-slate-500 text-xs">{new Date(an.created_at).toLocaleDateString('es-ES')}</p>
                </div>
                <div className="text-right">
                  <span className={`text-lg font-extrabold ${(an.overall_score || 0) >= 60 ? 'text-green-400' : (an.overall_score || 0) >= 30 ? 'text-amber-400' : 'text-red-400'}`}>
                    {an.overall_score || 0}
                  </span>
                  <p className={`text-xs font-semibold ${probColors[an.win_probability]?.split(' ')[0] || 'text-slate-400'}`}>{an.win_probability}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-12 text-center">
              <p className="text-slate-500">Seleccione un analisis o genere uno nuevo</p>
            </div>
          ) : a.error ? (
            <div className="bg-red-900/30 border border-red-700 p-4 rounded text-red-300 text-sm">{a.raw || a.error}</div>
          ) : (
            <div className="space-y-4">
              {/* Score Header */}
              <div className="flex gap-4 items-center">
                <div className={`w-20 h-20 rounded-xl flex flex-col items-center justify-center ${(a.overall_score || 0) >= 60 ? 'bg-green-900/40 border border-green-700' : (a.overall_score || 0) >= 30 ? 'bg-amber-900/40 border border-amber-700' : 'bg-red-900/40 border border-red-700'}`}>
                  <span className="text-2xl font-extrabold text-white">{a.overall_score || 0}</span>
                  <span className="text-[10px] text-slate-400">/100</span>
                </div>
                <div>
                  <p className="text-white text-lg font-bold">{selected.lead_name || 'Deal Analysis'}</p>
                  <div className="flex gap-2 mt-1">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${probColors[a.win_probability] || 'bg-slate-700 text-slate-300'}`}>
                      {a.win_probability || 'N/A'}
                    </span>
                    <span className="bg-slate-700 text-slate-300 px-3 py-1 rounded-full text-xs">{a.deal_stage || 'N/A'}</span>
                  </div>
                </div>
                <div className="ml-auto">
                  <Button variant="outline" size="sm" className="text-xs text-red-400 border-red-600" onClick={() => handleDelete(selected.id)}>Eliminar</Button>
                </div>
              </div>

              {/* MEDDPICC Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { key: 'metrics', label: 'M - Metricas' },
                  { key: 'economic_buyer', label: 'E - Economic Buyer' },
                  { key: 'decision_criteria', label: 'D - Decision Criteria' },
                  { key: 'decision_process', label: 'D - Decision Process' },
                  { key: 'paper_process', label: 'P - Paper Process' },
                  { key: 'identify_pain', label: 'I - Identify Pain' },
                  { key: 'champion', label: 'C - Champion' },
                  { key: 'competition', label: 'C - Competition' },
                ].map(({ key, label }) => (
                  <MeddpiccGauge key={key} label={label}
                    score={m[key]?.score || 0}
                    analysis={m[key]?.analysis || ''}
                    action={m[key]?.action || ''} />
                ))}
              </div>

              {/* Actions + Risks + Strategy */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card className="bg-green-900/20 border-green-800">
                  <CardHeader className="pb-2"><CardTitle className="text-green-300 text-xs uppercase">Acciones Recomendadas</CardTitle></CardHeader>
                  <CardContent>
                    {(a.recommended_actions || []).map((act, i) => (
                      <p key={i} className="text-slate-300 text-xs mb-1">{i + 1}. {act}</p>
                    ))}
                  </CardContent>
                </Card>
                <Card className="bg-red-900/20 border-red-800">
                  <CardHeader className="pb-2"><CardTitle className="text-red-300 text-xs uppercase">Riesgos</CardTitle></CardHeader>
                  <CardContent>
                    {(a.risks || []).map((r, i) => (
                      <p key={i} className="text-slate-300 text-xs mb-1">{r}</p>
                    ))}
                  </CardContent>
                </Card>
                <Card className="bg-violet-900/20 border-violet-800">
                  <CardHeader className="pb-2"><CardTitle className="text-violet-300 text-xs uppercase">Estrategia de Cierre</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-slate-300 text-xs">{a.closing_strategy || 'N/A'}</p>
                  </CardContent>
                </Card>
              </div>

              {a.next_steps && (
                <div className="bg-indigo-900/20 border border-indigo-800 rounded-lg p-4">
                  <p className="text-indigo-300 text-xs font-semibold uppercase mb-1">Proximos Pasos (48h)</p>
                  <p className="text-slate-300 text-sm">{a.next_steps}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
